/**
 * PaymentStep.js
 *
 * Step 4 of the clearance wizard.
 * Handles payment initiation and displays payment status.
 * User cannot go back from this step once selections are confirmed.
 *
 * @component
 */
import React, { useState } from "react";
import {
  Button,
  Space,
  Typography,
  Divider,
  Alert,
  Descriptions,
  Tag,
  Card,
  Result,
} from "antd";
import {
  CreditCardOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  HomeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { message } from "antd";
import SurePayPaymentModal from "../../../../components/finance/payments/SurePayPaymentModal";

const { Text, Title } = Typography;

/**
 * PaymentStep Component
 *
 * @param {object} clearance - The clearance data
 * @param {function} onRefresh - Callback to refresh clearance data
 * @param {function} onBack - Callback to return to students list
 */
const PaymentStep = ({ clearance, onRefresh, onBack }) => {
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  /**
   * Refresh payment status
   */
  const handleRefreshStatus = async () => {
    message.loading("Checking payment status...");
    await onRefresh();
    message.destroy();
  };

  if (!clearance) return null;

  const balance = parseFloat(clearance.balance || 0);
  const totalAmount = parseFloat(clearance.total_amount || 0);
  const amountPaid = parseFloat(clearance.amount_paid || 0);
  const isPaid = balance <= 0;
  const isPartiallyPaid = amountPaid > 0 && balance > 0;

  // If fully paid, show success state
  if (isPaid) {
    return (
      <Result
        status="success"
        icon={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
        title="Payment Complete!"
        subTitle={`Total paid: UGX ${totalAmount.toLocaleString()}`}
        extra={[
          <Button
            type="primary"
            size="large"
            key="back"
            icon={<HomeOutlined />}
            onClick={onBack}
          >
            Back to Students
          </Button>,
        ]}
      >
        <Descriptions
          bordered
          column={1}
          style={{ maxWidth: 400, margin: "0 auto" }}
        >
          <Descriptions.Item label="Student">
            {clearance.student_name}
          </Descriptions.Item>
          <Descriptions.Item label="BOTPC">
            <Text code>{clearance.botpc}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color="success">{clearance.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Amount Paid">
            <Text strong type="success">
              UGX {amountPaid.toLocaleString()}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </Result>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>
          <CreditCardOutlined style={{ marginRight: 8 }} />
          Complete Payment
        </Title>
        <Text type="secondary">
          {isPartiallyPaid
            ? "You have a partial payment. Complete the remaining balance below."
            : "Make your payment to complete the clearance process."}
        </Text>
      </div>

      {/* Payment Status Alert */}
      {isPartiallyPaid ? (
        <Alert
          message="Partial Payment Received"
          description={`You have paid UGX ${amountPaid.toLocaleString()}. Please pay the remaining balance of UGX ${balance.toLocaleString()}.`}
          type="warning"
          showIcon
          icon={<ClockCircleOutlined />}
          style={{ marginBottom: 24 }}
        />
      ) : (
        <Alert
          message="Payment Required"
          description="Click the button below to make your payment."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Payment Summary Card */}
      <Card style={{ marginBottom: 24 }}>
        <Descriptions
          title="Payment Details"
          bordered
          column={{ xs: 1, sm: 2 }}
        >
          <Descriptions.Item label="Student">
            {clearance.student_name}
          </Descriptions.Item>
          <Descriptions.Item label="BOTPC">
            <Text code copyable>
              {clearance.botpc}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={isPartiallyPaid ? "warning" : "processing"}>
              {clearance.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Items Selected">
            {clearance.selections?.length || 0} items
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Amount Summary */}
      <div
        style={{
          padding: 24,
          background: "#f6ffed",
          borderRadius: 8,
          border: "1px solid #b7eb8f",
          marginBottom: 24,
        }}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text style={{ fontSize: 16 }}>Total Amount:</Text>
            <Text strong style={{ fontSize: 18 }}>
              UGX {totalAmount.toLocaleString()}
            </Text>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text style={{ fontSize: 16 }}>Amount Paid:</Text>
            <Text type="success" style={{ fontSize: 18 }}>
              UGX {amountPaid.toLocaleString()}
            </Text>
          </div>

          <Divider style={{ margin: "8px 0" }} />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text strong style={{ fontSize: 18 }}>
              Balance Due:
            </Text>
            <Text strong type="danger" style={{ fontSize: 28 }}>
              UGX {balance.toLocaleString()}
            </Text>
          </div>
        </Space>
      </div>

      {/* Deadline Warning */}
      {clearance.payment_deadline && (
        <Alert
          message={`Payment Deadline: ${clearance.payment_deadline}`}
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Action Buttons */}
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Button
          type="primary"
          size="large"
          icon={<DollarOutlined />}
          onClick={() => setPaymentModalOpen(true)}
          block
          style={{ height: 50, fontSize: 16 }}
        >
          {isPartiallyPaid
            ? `Pay Remaining UGX ${balance.toLocaleString()}`
            : `Pay UGX ${balance.toLocaleString()}`}
        </Button>

        <Button
          size="large"
          icon={<ReloadOutlined />}
          onClick={handleRefreshStatus}
          block
        >
          Refresh Payment Status
        </Button>

        <Button type="link" icon={<HomeOutlined />} onClick={onBack} block>
          Return to Students List
        </Button>
      </Space>

      {/* Payment Instructions */}
      <Divider />
      <Alert
        message="How to Pay"
        description={
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>
              Click the pay button and enter your MTN or Airtel mobile money
              number
            </li>
            <li>Send the prompt to your phone</li>
            <li>Authorize the payment by entering your mobile money PIN</li>
            <li>
              Click "Refresh Payment Status" to confirm your payment was
              received
            </li>
          </ol>
        }
        type="info"
        style={{ marginTop: 16 }}
      />
      <SurePayPaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onSuccess={async () => {
          setPaymentModalOpen(false);
          await onRefresh();
        }}
        paymentCode={clearance.botpc}
        codeType="BOTPC"
        amount={balance}
        description={`BOT Clearance Payment - ${clearance.botpc}`}
        studentName={clearance.student_name}
        feeLabel="BOT Clearance"
        title="Pay BOT Clearance"
      />
    </div>
  );
};

export default PaymentStep;
